import { AgCartesianChartOptions } from "ag-charts-community";
import { useMemo } from "react";
import colors from "tailwindcss/colors";

import { baseAnalyticsChartOptions } from "@/components/RequestsAnalytics/chartOptions/base";
import {
  AXIS_LABEL_COLOR,
  AXIS_LINE_COLOR,
  CHART_AXIS_TITLE_FONT_SIZE,
  GRID_LINE_COLOR,
} from "@/components/RequestsAnalytics/constants";
import { isRecord } from "@/components/RequestsAnalytics/utils/isRecord";
import {
  GetRequestVolumeTailResponse,
  type RequestVolumeTailBucket,
  type RequestVolumeTailTemplateSummary,
} from "@/queries/signals";

const MAX_VISIBLE_SIGNALS = 5;
export const MAX_GROUPED_TEMPLATES = 4;
export const OTHER_INVOCATION_SITES_KEY = "other-sources";
const NO_SIGNAL_COLOR = colors.blue[500];
const OTHER_SIGNALS_COLOR = colors.slate[300];

const SIGNAL_PALETTE = [
  colors.violet[400],
  colors.rose[400],
  colors.amber[400],
  colors.cyan[400],
  colors.emerald[400],
  colors.indigo[400],
  colors.pink[400],
  colors.teal[400],
].filter(Boolean) as string[];

export const getRequestVolumeSignalColor = (signalId: number) =>
  SIGNAL_PALETTE[Math.abs(signalId) % SIGNAL_PALETTE.length] ?? colors.violet[400];

export const getRequestVolumeInvocationSiteKey = (promptId: number | null) =>
  promptId === null ? "direct" : String(promptId);

interface VisibleSignal {
  id: number;
  name: string;
  color: string;
}

interface ChartScope {
  key: string;
  name: string;
  promptId: number | null;
  directOnly: boolean;
  isAggregate: boolean;
  buckets: RequestVolumeTailBucket[];
}

interface ChartRow {
  bucketKey: string;
  selectedSignalRequests: number;
  [key: string]: string | number;
}

interface TooltipFocus {
  label: string;
  color: string;
  getCount: (bucket: RequestVolumeTailBucket) => number;
  description: string;
}

interface SegmentScope {
  promptId?: number;
  promptName?: string;
  directOnly?: boolean;
}

const createTimeLabelFormatter = () =>
  new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

const escapeTooltipText = (value: string) =>
  value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");

const getScopeFieldKey = (scope: ChartScope, field: string) => `scope_${scope.key}_${field}`;
const getScopeSignalKey = (scope: ChartScope, signalId: number) =>
  getScopeFieldKey(scope, `signal_${signalId}`);

const buildTooltipHtml = ({
  timestamp,
  scopeName,
  totalRequests,
  count,
  label,
  color,
  description,
}: {
  timestamp: string;
  scopeName?: string;
  totalRequests: number;
  count: number;
  label: string;
  color: string;
  description: string;
}) =>
  `<div style="box-sizing:border-box;width:252px;padding:10px 12px;font-size:12px;line-height:1.4;">` +
  `<div style="color:#6b7280;font-size:11px;margin-bottom:4px;">${timestamp}</div>` +
  (scopeName
    ? `<div style="color:#374151;font-size:11px;font-weight:600;margin-bottom:7px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeTooltipText(scopeName)}</div>`
    : "") +
  `<div style="display:flex;align-items:center;gap:7px;">` +
  `<span style="width:8px;height:8px;flex:none;border-radius:2px;background:${color};display:inline-block;"></span>` +
  `<strong style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px;">${escapeTooltipText(label)}</strong></div>` +
  `<div style="margin-top:5px;color:#4b5563;"><strong>${count}</strong> requests</div>` +
  `<div style="margin-top:2px;color:#9ca3af;">${escapeTooltipText(description)}</div>` +
  `<div style="border-top:1px solid #f3f4f6;margin-top:8px;padding-top:6px;color:#9ca3af;">${totalRequests} requests for this invocation site</div>` +
  `</div>`;

export type RequestVolumeTailChartSegment =
  | ({ type: "no_signal" } & SegmentScope)
  | ({ type: "signal"; signalId: number; signalName: string } & SegmentScope)
  | ({ type: "selected_signal"; signalId: number; signalName: string } & SegmentScope)
  | ({ type: "other_signals" } & SegmentScope);

interface RequestVolumeTailChartInteractions {
  selectedSignal?: { id: number; name: string } | null;
  hiddenInvocationSiteKeys?: string[];
  onSegmentClick?: (
    bucket: RequestVolumeTailBucket,
    segment: RequestVolumeTailChartSegment,
  ) => void;
}

const getVisibleSignals = (data: GetRequestVolumeTailResponse | undefined): VisibleSignal[] => {
  const countsBySignalId = new Map<number, number>();

  for (const bucket of data?.buckets ?? []) {
    for (const signalCount of bucket.signal_distribution.assigned_signal_counts) {
      countsBySignalId.set(
        signalCount.signal_id,
        (countsBySignalId.get(signalCount.signal_id) ?? 0) + signalCount.count,
      );
    }
  }

  return (data?.signals ?? [])
    .map(signal => ({
      id: signal.id,
      name: signal.name,
      count: countsBySignalId.get(signal.id) ?? 0,
      color: getRequestVolumeSignalColor(signal.id),
    }))
    .filter(signal => signal.count > 0)
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
    .slice(0, MAX_VISIBLE_SIGNALS)
    .map(({ id, name, color }) => ({ id, name, color }));
};

const templateScope = (summary: RequestVolumeTailTemplateSummary): ChartScope => ({
  key: getRequestVolumeInvocationSiteKey(summary.prompt_id),
  name: summary.prompt_name,
  promptId: summary.prompt_id,
  directOnly: summary.prompt_id === null,
  isAggregate: false,
  buckets: summary.buckets,
});

interface AggregateBucketAccumulator {
  start: string;
  end: string;
  totalRequests: number;
  flaggedRequests: number;
  unflaggedCount: number;
  signalCounts: Map<number, number>;
  assignedSignalCounts: Map<number, number>;
}

const addSignalCounts = (
  target: Map<number, number>,
  counts: { signal_id: number; count: number }[],
) => {
  for (const signalCount of counts) {
    target.set(signalCount.signal_id, (target.get(signalCount.signal_id) ?? 0) + signalCount.count);
  }
};

const aggregateTemplateBuckets = (
  summaries: RequestVolumeTailTemplateSummary[],
): RequestVolumeTailBucket[] => {
  const bucketsByStart = new Map<string, AggregateBucketAccumulator>();

  for (const summary of summaries) {
    for (const bucket of summary.buckets) {
      let aggregate = bucketsByStart.get(bucket.start);
      if (!aggregate) {
        aggregate = {
          start: bucket.start,
          end: bucket.end,
          totalRequests: 0,
          flaggedRequests: 0,
          unflaggedCount: 0,
          signalCounts: new Map(),
          assignedSignalCounts: new Map(),
        };
        bucketsByStart.set(bucket.start, aggregate);
      }

      aggregate.totalRequests += bucket.total_requests;
      aggregate.flaggedRequests += bucket.flagged_requests;
      aggregate.unflaggedCount += bucket.signal_distribution.unflagged_count;
      addSignalCounts(aggregate.signalCounts, bucket.signal_counts);
      addSignalCounts(
        aggregate.assignedSignalCounts,
        bucket.signal_distribution.assigned_signal_counts,
      );
    }
  }

  return [...bucketsByStart.values()]
    .sort((left, right) => left.start.localeCompare(right.start))
    .map(bucket => ({
      start: bucket.start,
      end: bucket.end,
      total_requests: bucket.totalRequests,
      flagged_requests: bucket.flaggedRequests,
      signal_counts: [...bucket.signalCounts.entries()].map(([signal_id, count]) => ({
        signal_id,
        count,
      })),
      signal_distribution: {
        unflagged_count: bucket.unflaggedCount,
        assigned_signal_counts: [...bucket.assignedSignalCounts.entries()].map(
          ([signal_id, count]) => ({ signal_id, count }),
        ),
      },
    }));
};

const getChartScopes = (
  data: GetRequestVolumeTailResponse | undefined,
  hiddenInvocationSiteKeys: string[],
): ChartScope[] => {
  if (data?.scoped_prompt) {
    return [
      {
        key: String(data.scoped_prompt.prompt_id),
        name: data.scoped_prompt.prompt_name,
        promptId: data.scoped_prompt.prompt_id,
        directOnly: data.scoped_prompt.prompt_id === null,
        isAggregate: false,
        buckets: data.buckets,
      },
    ];
  }

  const hiddenKeys = new Set(hiddenInvocationSiteKeys);
  const sortedTemplateSummaries = [...(data?.template_summaries ?? [])]
    .filter(summary => summary.total_requests > 0)
    .sort((left, right) => right.total_requests - left.total_requests);

  let availableTemplateScopes = sortedTemplateSummaries.map(templateScope);
  if (sortedTemplateSummaries.length > MAX_GROUPED_TEMPLATES) {
    const primaryTemplateCount = MAX_GROUPED_TEMPLATES - 1;
    const otherTemplateSummaries = sortedTemplateSummaries.slice(primaryTemplateCount);
    availableTemplateScopes = [
      ...sortedTemplateSummaries.slice(0, primaryTemplateCount).map(templateScope),
      {
        key: OTHER_INVOCATION_SITES_KEY,
        name: "Other sources",
        promptId: null,
        directOnly: false,
        isAggregate: true,
        buckets: aggregateTemplateBuckets(otherTemplateSummaries),
      },
    ];
  }

  if (availableTemplateScopes.length > 0) {
    return availableTemplateScopes.filter(scope => !hiddenKeys.has(scope.key));
  }

  return [
    {
      key: "all",
      name: "All requests",
      promptId: null,
      directOnly: false,
      isAggregate: false,
      buckets: data?.buckets ?? [],
    },
  ];
};

export const useRequestVolumeTailChartOptions = (
  data: GetRequestVolumeTailResponse | undefined,
  interactions: RequestVolumeTailChartInteractions = {},
): AgCartesianChartOptions => {
  const { onSegmentClick, selectedSignal = null, hiddenInvocationSiteKeys = [] } = interactions;

  return useMemo(() => {
    const formatter = createTimeLabelFormatter();
    const axisLabels = new Map<string, string>();
    const visibleSignals = getVisibleSignals(data);
    const selectedSignalColor = selectedSignal
      ? getRequestVolumeSignalColor(selectedSignal.id)
      : colors.violet[400];
    const visibleSignalIds = visibleSignals.map(signal => signal.id);
    const scopes = getChartScopes(data, hiddenInvocationSiteKeys);
    const scopeBucketsByStart = new Map(
      scopes.map(scope => [
        scope.key,
        new Map(scope.buckets.map(bucket => [bucket.start, bucket])),
      ]),
    );
    const segmentByYKey = new Map<string, RequestVolumeTailChartSegment>();
    const signalCountCache = new WeakMap<RequestVolumeTailBucket, Map<number, number>>();
    const assignedSignalCountCache = new WeakMap<RequestVolumeTailBucket, Map<number, number>>();

    const getSignalCount = (bucket: RequestVolumeTailBucket, signalId: number) => {
      let countsBySignalId = signalCountCache.get(bucket);
      if (!countsBySignalId) {
        countsBySignalId = new Map(
          bucket.signal_counts.map(count => [count.signal_id, count.count]),
        );
        signalCountCache.set(bucket, countsBySignalId);
      }
      return countsBySignalId.get(signalId) ?? 0;
    };

    const getAssignedSignalCount = (bucket: RequestVolumeTailBucket, signalId: number) => {
      let countsBySignalId = assignedSignalCountCache.get(bucket);
      if (!countsBySignalId) {
        countsBySignalId = new Map(
          bucket.signal_distribution.assigned_signal_counts.map(count => [
            count.signal_id,
            count.count,
          ]),
        );
        assignedSignalCountCache.set(bucket, countsBySignalId);
      }
      return countsBySignalId.get(signalId) ?? 0;
    };

    const chartRows: ChartRow[] = (data?.buckets ?? []).map(bucket => {
      axisLabels.set(bucket.start, formatter.format(new Date(bucket.start)));
      const selectedSignalRequests = selectedSignal ? getSignalCount(bucket, selectedSignal.id) : 0;
      const row: ChartRow = {
        bucketKey: bucket.start,
        selectedSignalRequests,
      };

      if (!selectedSignal) {
        for (const scope of scopes) {
          const scopeBucket = scopeBucketsByStart.get(scope.key)?.get(bucket.start);
          row[getScopeFieldKey(scope, "no_signal")] =
            scopeBucket?.signal_distribution.unflagged_count ?? 0;
          const visibleSignalTotal = visibleSignals.reduce(
            (total, signal) =>
              total + (scopeBucket ? getAssignedSignalCount(scopeBucket, signal.id) : 0),
            0,
          );
          row[getScopeFieldKey(scope, "other_signals")] = scopeBucket
            ? Math.max(0, scopeBucket.flagged_requests - visibleSignalTotal)
            : 0;
          for (const signal of visibleSignals) {
            row[getScopeSignalKey(scope, signal.id)] = scopeBucket
              ? getAssignedSignalCount(scopeBucket, signal.id)
              : 0;
          }
        }
      }

      return row;
    });

    const globalBucketByStart = new Map(
      (data?.buckets ?? []).map(bucket => [bucket.start, bucket]),
    );

    const buildBucketTooltip =
      (focus: TooltipFocus, scope?: ChartScope) =>
      ({ datum }: { datum: { bucketKey?: string } }): string => {
        const bucketKey = datum?.bucketKey ?? "";
        const bucket = scope
          ? scopeBucketsByStart.get(scope.key)?.get(bucketKey)
          : globalBucketByStart.get(bucketKey);
        if (!bucket) {
          return "";
        }

        return buildTooltipHtml({
          timestamp: `${formatter.format(new Date(bucket.start))}–${formatter.format(new Date(bucket.end))}`,
          scopeName: scope?.name,
          totalRequests: bucket.total_requests,
          count: focus.getCount(bucket),
          label: focus.label,
          color: focus.color,
          description: focus.description,
        });
      };

    const series = [];

    if (selectedSignal) {
      segmentByYKey.set("selectedSignalRequests", {
        type: "selected_signal",
        signalId: selectedSignal.id,
        signalName: selectedSignal.name,
      });
      series.push({
        type: "bar" as const,
        xKey: "bucketKey",
        yKey: "selectedSignalRequests",
        yName: selectedSignal.name,
        fill: selectedSignalColor,
        stroke: selectedSignalColor,
        cornerRadius: 2,
        cursor: onSegmentClick ? ("pointer" as const) : ("default" as const),
        nodeClickRange: "exact" as const,
        tooltip: {
          renderer: buildBucketTooltip({
            label: selectedSignal.name,
            color: selectedSignalColor,
            getCount: bucket => getSignalCount(bucket, selectedSignal.id),
            description: "Requests that matched the selected Signal.",
          }),
        },
      });
    } else {
      for (const scope of scopes) {
        let scopeSegment: SegmentScope = {};
        if (scope.directOnly) {
          scopeSegment = { directOnly: true, promptName: scope.name };
        } else if (scope.promptId !== null) {
          scopeSegment = { promptId: scope.promptId, promptName: scope.name };
        }
        const canClickScope = Boolean(onSegmentClick && !scope.isAggregate);
        const noSignalKey = getScopeFieldKey(scope, "no_signal");
        if (canClickScope) {
          segmentByYKey.set(noSignalKey, { type: "no_signal", ...scopeSegment });
        }
        series.push({
          type: "bar" as const,
          xKey: "bucketKey",
          yKey: noSignalKey,
          yName: `${scope.name} · No Signal match`,
          stacked: true,
          stackGroup: `scope-${scope.key}`,
          fill: NO_SIGNAL_COLOR,
          stroke: NO_SIGNAL_COLOR,
          cornerRadius: 2,
          cursor: canClickScope ? ("pointer" as const) : ("default" as const),
          nodeClickRange: "exact" as const,
          tooltip: {
            renderer: buildBucketTooltip(
              {
                label: "No Signal match",
                color: NO_SIGNAL_COLOR,
                getCount: bucket => bucket.signal_distribution.unflagged_count,
                description: "Requests without any true Signal result.",
              },
              scope,
            ),
          },
        });

        for (const signal of visibleSignals) {
          const yKey = getScopeSignalKey(scope, signal.id);
          if (canClickScope) {
            segmentByYKey.set(yKey, {
              type: "signal",
              signalId: signal.id,
              signalName: signal.name,
              ...scopeSegment,
            });
          }
          series.push({
            type: "bar" as const,
            xKey: "bucketKey",
            yKey,
            yName: `${scope.name} · ${signal.name}`,
            stacked: true,
            stackGroup: `scope-${scope.key}`,
            fill: signal.color,
            stroke: signal.color,
            cornerRadius: 2,
            cursor: canClickScope ? ("pointer" as const) : ("default" as const),
            nodeClickRange: "exact" as const,
            tooltip: {
              renderer: buildBucketTooltip(
                {
                  label: signal.name,
                  color: signal.color,
                  getCount: bucket => getAssignedSignalCount(bucket, signal.id),
                  description: "Requests assigned to this Signal in the distribution.",
                },
                scope,
              ),
            },
          });
        }

        const otherSignalsKey = getScopeFieldKey(scope, "other_signals");
        if (canClickScope) {
          segmentByYKey.set(otherSignalsKey, { type: "other_signals", ...scopeSegment });
        }
        series.push({
          type: "bar" as const,
          xKey: "bucketKey",
          yKey: otherSignalsKey,
          yName: `${scope.name} · Other Signals`,
          stacked: true,
          stackGroup: `scope-${scope.key}`,
          fill: OTHER_SIGNALS_COLOR,
          stroke: OTHER_SIGNALS_COLOR,
          cornerRadius: 2,
          cursor: canClickScope ? ("pointer" as const) : ("default" as const),
          nodeClickRange: "exact" as const,
          tooltip: {
            renderer: buildBucketTooltip(
              {
                label: "Other Signals",
                color: OTHER_SIGNALS_COLOR,
                getCount: bucket =>
                  Math.max(
                    0,
                    bucket.flagged_requests -
                      visibleSignalIds.reduce(
                        (total, signalId) => total + getAssignedSignalCount(bucket, signalId),
                        0,
                      ),
                  ),
                description: "Matched Signals outside the top visible set.",
              },
              scope,
            ),
          },
        });
      }
    }

    return {
      ...baseAnalyticsChartOptions,
      listeners: {
        ...(onSegmentClick
          ? {
              seriesNodeClick: (event: unknown) => {
                if (!isRecord(event) || !isRecord(event.datum) || typeof event.yKey !== "string") {
                  return;
                }
                if (isRecord(event.event) && typeof event.event.stopPropagation === "function") {
                  event.event.stopPropagation();
                }
                const { bucketKey } = event.datum;
                if (typeof bucketKey !== "string") {
                  return;
                }
                const segment = segmentByYKey.get(event.yKey);
                if (!segment) {
                  return;
                }
                const scopeKey = event.yKey.match(/^scope_([^_]+)_/)?.[1];
                const bucket = scopeKey
                  ? scopeBucketsByStart.get(scopeKey)?.get(bucketKey)
                  : globalBucketByStart.get(bucketKey);
                if (bucket) {
                  onSegmentClick(bucket, segment);
                }
              },
            }
          : {}),
      },
      padding: {
        top: 8,
        right: 12,
        bottom: 8,
        left: 16,
      },
      data: chartRows,
      tooltip: {
        ...baseAnalyticsChartOptions.tooltip,
        mode: "single",
        range: "exact",
        tracking: true,
      },
      legend: {
        enabled: false,
      },
      axes: [
        {
          type: "category",
          position: "bottom",
          label: {
            fontSize: CHART_AXIS_TITLE_FONT_SIZE,
            color: AXIS_LABEL_COLOR,
            formatter: ({ value, index }: { value: string | number; index: number }) => {
              const shouldShowLabel = index % 3 === 0 || index === chartRows.length - 1;
              return shouldShowLabel ? (axisLabels.get(String(value)) ?? String(value)) : "";
            },
            avoidCollisions: true,
            wrapping: "never",
          },
          line: {
            stroke: AXIS_LINE_COLOR,
          },
          tick: {
            enabled: false,
          },
          crosshair: {
            enabled: false,
            label: {
              enabled: false,
            },
          },
        },
        {
          type: "number",
          position: "left",
          keys: series.map(item => item.yKey),
          min: 0,
          nice: true,
          label: {
            enabled: true,
            fontSize: 10,
            color: AXIS_LABEL_COLOR,
          },
          line: {
            enabled: false,
          },
          tick: {
            enabled: false,
          },
          gridLine: {
            enabled: true,
            style: [
              {
                stroke: GRID_LINE_COLOR,
                lineDash: [3, 3],
              },
            ],
          },
          crosshair: {
            enabled: false,
            label: {
              enabled: false,
            },
          },
        },
      ],
      series,
    };
  }, [data, hiddenInvocationSiteKeys, onSegmentClick, selectedSignal]);
};
